import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Shul, ZmanimData } from '../../types';
import { getTodayString, getTomorrowString } from '../../utils/dateShortcuts';
import { getCurrentTimeHHMM, applyOffset } from '../../utils/timeUtils';
import { buildDashboardSchedule, scheduleRowKey } from '../../utils/dashboardSchedule';

interface MinyanimTodayProps {
  shuls: Shul[];
  todayZmanim: ZmanimData | null;
  tomorrowZmanim: ZmanimData | null;
  loading: boolean;
}

export default function MinyanimToday({ shuls, todayZmanim, tomorrowZmanim, loading }: MinyanimTodayProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';

  const now = getCurrentTimeHHMM();
  const todayDone = todayZmanim ? applyOffset(todayZmanim.Tzet, 10) < now : false;
  const activeZmanim = todayDone ? tomorrowZmanim : todayZmanim;
  const candleLighting = activeZmanim?.candleLighting;

  const { todayRows, tomorrowRows, showBreaker, nextKey } = buildDashboardSchedule(
    shuls,
    getTodayString(),
    getTomorrowString(),
    todayZmanim,
    tomorrowZmanim,
    now
  );

  const renderRow = (m: (typeof todayRows)[number]) => {
    const isNext = scheduleRowKey(m) === nextKey;
    const note = isHebrew ? m.noteHe || m.note : m.note || m.noteHe;
    return (
      <Link
        key={scheduleRowKey(m)}
        to={`/shul/${m.shulId}`}
        className={`flex items-center justify-between py-1.5 text-sm border-b border-ink/5 last:border-0 hover:opacity-70 transition-opacity ${
          isNext ? 'bg-fog rounded-[10px] px-2 -mx-2' : ''
        }`}
      >
        <span className="min-w-0 truncate">
          <span className="text-ink">{t(`prayers.${m.prayerType}`)}</span>
          <span className="text-ash">
            {' · '}
            {isHebrew ? m.shulNameHe : m.shulNameEn}
            {note ? ` · ${note}` : ''}
          </span>
        </span>
        <span className="font-semibold tabular-nums text-ink shrink-0 flex items-center ms-3">
          {isNext && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 me-1.5" />}
          {m.displayTime}
        </span>
      </Link>
    );
  };

  const isEmpty = !loading && todayRows.length === 0 && tomorrowRows.length === 0;

  return (
    <div className="bg-paper border border-ink/10 rounded-[16px] p-4 space-y-1">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="text-sm font-semibold text-ink">{t('home.minyanimHeading')}</h2>
        {candleLighting && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-[10px] bg-fog text-ink tabular-nums">
            {t('zmanim.candleLighting')}: {candleLighting}
          </span>
        )}
      </div>

      {loading && (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-8 rounded-[10px] bg-fog animate-pulse" />
          ))}
        </div>
      )}

      {isEmpty && <p className="text-sm text-ash py-2">{t('common.noMoreMinyans')}</p>}

      {!loading && !isEmpty && (
        <div className="space-y-0.5">
          {todayRows.map(renderRow)}

          {showBreaker && (
            <div className="flex items-center gap-2 py-2">
              <div className="flex-1 border-t border-ink/10" />
              <span className="text-xs text-ash uppercase tracking-wide">{t('home.tomorrow')}</span>
              <div className="flex-1 border-t border-ink/10" />
            </div>
          )}

          {tomorrowRows.map(renderRow)}
        </div>
      )}
    </div>
  );
}
